/**
 * 📐 내 표 설정 — 표 페이지 공용 hook.
 *
 * prefs (UserPreferences) 안에 표마다 5개 키 세트 (excelMode / columns / columnsKnown / order / widths)
 * 가 있는데, hook 호출 측에서 그 키 이름들과 prefs 객체만 넘기면 나머지 derived 값들과
 * handler 를 한 번에 반환.
 *
 * 사용:
 *   const t = useExcelModeTable({
 *     prefs: prefsQ.data,
 *     fullColumns,
 *     prefsKey: {
 *       excelMode: 'maintenanceTabExcelMode',
 *       columns: 'maintenanceTabColumns',
 *       columnsKnown: 'maintenanceTabColumnsKnown',
 *       order: 'maintenanceTabColumnOrder',
 *       widths: 'maintenanceTabColumnWidths',
 *     },
 *     patch: (next) => patchPrefs.mutate(next),
 *   });
 *   // t.excelMode, t.visibleColumns, t.columnWidths, t.handleReorder...
 */

import { useMemo } from 'react';
import type { Column } from '../components/DataTable';
import type { UserPreferences } from './types';
import { resolveActiveColumns, resolveColumnOrder } from './columnPrefs';

export interface ExcelTablePrefsKey {
  excelMode: keyof UserPreferences;
  columns: keyof UserPreferences;
  columnsKnown: keyof UserPreferences;
  order: keyof UserPreferences;
  widths: keyof UserPreferences;
}

export interface UseExcelModeTableArgs<C extends Column<unknown> & { defaultOn: boolean }> {
  prefs: UserPreferences | undefined;
  fullColumns: C[];
  prefsKey: ExcelTablePrefsKey;
  patch: (next: Partial<UserPreferences>) => void;
}

export interface UseExcelModeTableResult<C> {
  /** 편집 모드 ON/OFF flag (드래그·resize·⚙ 노출 여부). */
  excelMode: boolean;
  /** 현재 화면 풀세트 키 */
  fullAllKeys: string[];
  /** defaultOn=true 인 키 */
  fullDefaultKeys: string[];
  /** 표시할 컬럼 키 Set */
  activeColKeys: Set<string>;
  /** 적용된 컬럼 순서 */
  orderedKeys: string[];
  /** 표시할 컬럼들 (순서·visibility 적용) */
  visibleColumns: C[];
  /** 사용자 지정 너비 (px) */
  columnWidths: Record<string, number> | undefined;
  /** 사용자가 prefs 를 가지고 있는지 (초기화 버튼 표시 조건) */
  hasPrefs: boolean;

  setExcelMode: (next: boolean) => void;
  toggleColumn: (key: string) => void;
  handleReorder: (fromKey: string, toKey: string, position: 'before' | 'after') => void;
  handleMoveLeft: (key: string) => void;
  handleMoveRight: (key: string) => void;
  handleResize: (key: string, widthPx: number) => void;
  handleReset: () => void;
}

export function useExcelModeTable<
  C extends Column<unknown> & { defaultOn: boolean },
>(args: UseExcelModeTableArgs<C>): UseExcelModeTableResult<C> {
  const { prefs, fullColumns, prefsKey, patch } = args;

  const excelMode = !!(prefs?.[prefsKey.excelMode] as boolean | undefined);
  const columnsPref = prefs?.[prefsKey.columns] as string[] | undefined;
  const columnsKnownPref = prefs?.[prefsKey.columnsKnown] as string[] | undefined;
  const orderPref = prefs?.[prefsKey.order] as string[] | undefined;
  const widthsPref = prefs?.[prefsKey.widths] as Record<string, number> | undefined;

  const fullAllKeys = useMemo(() => fullColumns.map((c) => c.key), [fullColumns]);
  const fullDefaultKeys = useMemo(
    () => fullColumns.filter((c) => c.defaultOn).map((c) => c.key),
    [fullColumns],
  );

  const activeColKeys = useMemo<Set<string>>(
    () => resolveActiveColumns(columnsPref, columnsKnownPref, fullAllKeys, fullDefaultKeys),
    [columnsPref, columnsKnownPref, fullAllKeys, fullDefaultKeys],
  );

  const orderedKeys = useMemo<string[]>(
    () => resolveColumnOrder(orderPref, fullAllKeys),
    [orderPref, fullAllKeys],
  );

  const visibleColumns = useMemo<C[]>(() => {
    const map = new Map(fullColumns.map((c) => [c.key, c]));
    return orderedKeys
      .filter((k) => activeColKeys.has(k))
      .map((k) => map.get(k))
      .filter((c): c is C => !!c);
  }, [orderedKeys, activeColKeys, fullColumns]);

  const hasPrefs =
    !!prefs &&
    (!!columnsPref?.length || !!orderPref?.length || !!widthsPref || excelMode);

  function setExcelMode(next: boolean) {
    patch({ [prefsKey.excelMode]: next } as Partial<UserPreferences>);
  }
  function toggleColumn(key: string) {
    const next = new Set(activeColKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    patch({
      [prefsKey.columns]: [...next],
      [prefsKey.columnsKnown]: [...fullAllKeys],
    } as Partial<UserPreferences>);
  }
  function handleReorder(fromKey: string, toKey: string, position: 'before' | 'after') {
    const filtered = orderedKeys.filter((k) => k !== fromKey);
    const idx = filtered.indexOf(toKey);
    if (idx < 0) return;
    filtered.splice(position === 'before' ? idx : idx + 1, 0, fromKey);
    patch({ [prefsKey.order]: filtered } as Partial<UserPreferences>);
  }
  function handleMoveLeft(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx <= 0) return;
    const prev = visKeys[idx - 1];
    if (prev) handleReorder(key, prev, 'before');
  }
  function handleMoveRight(key: string) {
    const visKeys = visibleColumns.map((c) => c.key);
    const idx = visKeys.indexOf(key);
    if (idx < 0 || idx >= visKeys.length - 1) return;
    const next = visKeys[idx + 1];
    if (next) handleReorder(key, next, 'after');
  }
  function handleResize(key: string, widthPx: number) {
    const next = { ...(widthsPref ?? {}), [key]: widthPx };
    patch({ [prefsKey.widths]: next } as Partial<UserPreferences>);
  }
  function handleReset() {
    // 모든 키 null → 백엔드 PATCH 에서 키 제거 (preferences.ts 의 null 처리)
    patch({
      [prefsKey.excelMode]: false,
      [prefsKey.columns]: null,
      [prefsKey.columnsKnown]: null,
      [prefsKey.order]: null,
      [prefsKey.widths]: null,
    } as unknown as Partial<UserPreferences>);
  }

  return {
    excelMode,
    fullAllKeys,
    fullDefaultKeys,
    activeColKeys,
    orderedKeys,
    visibleColumns,
    columnWidths: widthsPref,
    hasPrefs,
    setExcelMode,
    toggleColumn,
    handleReorder,
    handleMoveLeft,
    handleMoveRight,
    handleResize,
    handleReset,
  };
}
